import React, { useState } from "react";
import { Button, Card, Empty, Flex, notification, Popconfirm, Space, Switch, Tag, Tooltip, Typography } from "antd";
import { DeleteFilled, EditOutlined, PlusOutlined, EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import RuleModal from "./RuleModal";

const { Text } = Typography;

function RulesList({ rules, onUpdateRules }) {
  const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
  const [currentRule, setCurrentRule] = useState(null);

  const openRuleModal = (rule = null) => {
    setCurrentRule(rule);
    setIsRuleModalOpen(true);
  };

  const closeRuleModal = () => {
    setIsRuleModalOpen(false);
    setCurrentRule(null);
  };

  const getRuleId = (rule) => rule?._id || rule?.id;

  const handleSaveRule = (ruleData) => {
    let newRule;

    if (currentRule) {
      newRule = {
        ...ruleData,
        _id: ruleData._id || getRuleId(currentRule)
      };
    } else {

      newRule = {
        ...ruleData,
        _id: `rule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };
    }

    let updatedRules;
    if (currentRule) {
      updatedRules = rules.map(r =>
        String(getRuleId(r)) === String(getRuleId(currentRule)) ? newRule : r
      );
    } else {
      updatedRules = [...rules, newRule];
    }

    onUpdateRules(updatedRules);

    notification.success({
      message: 'Success',
      description: currentRule ? 'Rule updated successfully' : 'Rule added successfully',
      duration: 3,
      placement: 'topRight'
    });

    closeRuleModal();
  };

  const handleDeleteRule = (ruleId) => {
    const updatedRules = rules.filter(r => String(getRuleId(r)) !== String(ruleId));
    onUpdateRules(updatedRules);
    notification.success({
      message: 'Success',
      description: 'Rule deleted successfully',
      duration: 3,
      placement: 'topRight'
    });
  };

  const handleToggleRuleStatus = (ruleId) => {
    const updatedRules = rules.map(r =>
      String(getRuleId(r)) === String(ruleId) ? { ...r, isActive: !r.isActive } : r
    );
    onUpdateRules(updatedRules);
  };

  const getNormalizedJobNames = (rule) => {
    return (rule.jobs || []).map((job) => (
      job.includes('::') ? job.split('::')[1] : job
    ));
  };

  const getJobsPreview = (rule, limit = 2) => {
    const jobNames = getNormalizedJobNames(rule);
    if (jobNames.length === 0) {
      return null;
    }

    if (jobNames.length <= limit) {
      return jobNames.join(', ');
    }

    return `${jobNames.slice(0, limit).join(', ')} +${jobNames.length - limit} more`;
  };

  const getRuleDescription = (rule) => {
    const parts = [];
    parts.push(rule.trade);

    if (rule.serviceType === 'all') {
      parts.push('All Services');
    } else {
      parts.push(rule.serviceType.charAt(0).toUpperCase() + rule.serviceType.slice(1));
    }

    if (rule.jobs && rule.jobs.length > 0) {
      if (rule.jobs.length === 1) {
        const jobName = getNormalizedJobNames(rule)[0];
        parts.push(jobName);
      } else {
        parts.push(`${rule.jobs.length} jobs`);
      }
    } else {
      parts.push('All Jobs');
    }

    return parts.join(' → ');
  };

  return (
    <>
      <div className="flex flex-col gap-4 w-full items-start" style={{ marginTop: "16px" }}>
        <Flex justify="space-between" align="center" style={{ width: "100%" }}>
          <div>
            <label style={{ fontWeight: "bold", fontSize: "14px", display: "block" }}>
              Travel Add-On Rules
            </label>
            <Text type="secondary" style={{ fontSize: "13px" }}>
              Configure travel add-on notices based on trade, service type, and jobs
            </Text>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openRuleModal()}
            style={{
              borderRadius: "6px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
            }}
          >
            Add Rule
          </Button>
        </Flex>

        {rules.length > 0 ? (
          <div className="w-full" style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {rules.map((rule, index) => {
              const ruleId = getRuleId(rule);

              return (
                <Card
                  key={ruleId}
                  size="small"
                  style={{
                    borderRadius: "8px",
                    border: rule.isActive ? "1px solid #d9d9d9" : "1px solid #f0f0f0",
                    backgroundColor: rule.isActive ? "#ffffff" : "#fafafa",
                    boxShadow: rule.isActive ? "0 2px 4px rgba(0,0,0,0.06)" : "none",
                    opacity: rule.isActive ? 1 : 0.7,
                    transition: "all 0.3s ease"
                  }}
                  onMouseEnter={(e) => {
                    if (rule.isActive) {
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (rule.isActive) {
                      e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.06)";
                    }
                  }}
                >
                  <Flex justify="space-between" align="flex-start">
                    <div style={{ flex: 1 }}>
                      <Flex align="center" gap={8} style={{ marginBottom: "8px", flexWrap: "wrap" }}>
                        <Tag color={rule.isActive ? "blue" : "default"} style={{ margin: 0 }}>
                          Rule #{index + 1}
                        </Tag>

                        <Text strong style={{ fontSize: "14px", color: rule.isActive ? "#262626" : "#8c8c8c" }}>
                          {getRuleDescription(rule)}
                        </Text>
                        {rule.jobs?.length > 1 && (
                          <Tooltip title={getNormalizedJobNames(rule).join(', ')}>
                            <Tag color="cyan" style={{ margin: 0, fontSize: "11px" }}>
                              Jobs: {getJobsPreview(rule)}
                            </Tag>
                          </Tooltip>
                        )}

                        <Tooltip title={rule.showForMembers ? "Visible to members" : "Hidden from members"}>
                          <Tag
                            icon={rule.showForMembers ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                            color={rule.showForMembers ? "green" : "red"}
                            style={{ margin: 0, fontSize: "11px" }}
                          >
                            {rule.showForMembers ? "Visible to Members" : "Hidden from Members"}
                          </Tag>
                        </Tooltip>
                      </Flex>

                      <div style={{ marginTop: "8px" }}>
                        <Text
                          strong
                          style={{
                            fontSize: "12px",
                            color: rule.isActive ? "#8c8c8c" : "#bfbfbf",
                            display: "block",
                            marginBottom: "4px"
                          }}
                        >
                          Travel Add-On Notice:
                        </Text>
                        <Text
                          style={{
                            fontSize: "13px",
                            color: rule.isActive ? "#595959" : "#8c8c8c",
                            display: "block"
                          }}
                        >
                          {rule.message}
                        </Text>
                      </div>
                    </div>
                    <Space>
                      <Tooltip title={rule.isActive ? "Disable Rule" : "Enable Rule"}>
                        <Switch
                          size="small"
                          checked={rule.isActive}
                          onChange={() => handleToggleRuleStatus(ruleId)}
                        />
                      </Tooltip>
                      <Tooltip title="Edit Rule">
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          size="small"
                          onClick={() => openRuleModal(rule)}
                        />
                      </Tooltip>
                      <Tooltip title="Delete Rule">
                        <Popconfirm
                          title="Delete Rule?"
                          description="Are you sure you want to delete this rule?"
                          onConfirm={() => handleDeleteRule(ruleId)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button
                            type="text"
                            danger
                            icon={<DeleteFilled />}
                            size="small"
                          />
                        </Popconfirm>
                      </Tooltip>
                    </Space>
                  </Flex>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card
            style={{
              width: "100%",
              textAlign: "center",
              borderStyle: "dashed",
              backgroundColor: "#fafafa"
            }}
          >
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span style={{ color: "#8c8c8c" }}>
                  No rules are configured yet. Add a rule to customize travel add-on notices for specific trades and services, or to make them visible only to members.
                </span>
              }
            />
          </Card>
        )}
      </div>

      <RuleModal
        isOpen={isRuleModalOpen}
        currentRule={currentRule}
        onSave={handleSaveRule}
        onClose={closeRuleModal}
        existingRules={rules}
      />
    </>
  );
}

export default RulesList;
